<script setup lang="ts">
import type { FormDto, UserContribution, AIInsights, Cluster, Locales } from '@/services/types/api.types';
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuasar } from 'quasar';
import { useField } from 'vee-validate';

import { getTranslation } from '@/composables/data-display-utils';
import { useInsightsDragDrop } from '@/composables/useInsightsDragDrop';
import { useInsightsThemeManagement } from '@/composables/useInsightsThemeManagement';
import { useIdeaActions } from '@/composables/useIdeaActions';

import AnalysisHeader from '@/components/insights/AnalysisHeader.vue';
import AnalysisSummaryCards from '@/components/insights/AnalysisSummaryCards.vue';
import AnalysisEmptyState from '@/components/insights/AnalysisEmptyState.vue';
import ThemeCard from '@/components/insights/ThemeCard.vue';
import MoveIdeaDialog from '@/components/insights/dialogs/MoveIdeaDialog.vue';
import MergeThemeDialog from '@/components/insights/dialogs/MergeThemeDialog.vue';
import MergeIdeaDialog from '@/components/insights/dialogs/MergeIdeaDialog.vue';
import ContributionsDialog from '@/components/insights/dialogs/ContributionsDialog.vue';

const $q = useQuasar();
const { t, locale } = useI18n();

const props = defineProps<{
  form: FormDto;
  languageEnabled: Locales[];
  selectedAnalysisLanguage?: Locales | null;
  contributions: UserContribution[];
  isAnalyzing?: boolean;
  isResetting?: boolean;
  error?: string | null;
}>();

const emit = defineEmits<{
  (e: 'save'): void;
  (e: 'analyze', language?: Locales): void;
  (e: 'update:selectedAnalysisLanguage', language: Locales): void;
  (e: 'reset'): void;
}>();

const { value: insights } = useField<AIInsights | null>('ai_insights');

const dragDrop = useInsightsDragDrop(insights, save);
const ideaActions = useIdeaActions(
  insights,
  computed(() => props.contributions),
  dragDrop,
  t,
  (opts) => $q.notify(opts),
);
const {
  moveDialog,
  moveIdeaContext,
  moveThemeOptions,
  moveTargetTheme,
  openMoveIdeaDialog,
  confirmMoveIdea,
  mergeThemeDialog,
  mergeThemeOptions,
  mergeTargetTheme,
  mergeSourceTheme,
  openMergeThemeDialog,
  confirmMergeTheme,
  mergeIdeaDialog,
  mergeIdeaOptions,
  mergeIdeaTarget,
  mergeIdeaTargetId,
  mergeIdeaContext,
  mergeIdeaSearch,
  openMergeIdeaDialog,
  confirmMergeIdea,
  contributionsDialog,
  contributionsContext,
  contributionsList,
  expandedContributions,
  openContributionsDialog,
  toggleContribution,
} = ideaActions;

function save() {
  emit('save');
}

// Use composables for drag-drop and theme management
const themeManagement = useInsightsThemeManagement(insights, save);

// Computed properties for status and summary
const analysisStatus = computed(() => {
  if (props.isAnalyzing) return 'analyzing';
  if (insights.value?.clusters && insights.value.clusters.length > 0) return 'analyzed';
  return 'not_analyzed';
});

const themesCount = computed(() => insights.value?.clusters?.length || 0);
const ideasCount = computed(() => {
  return insights.value?.clusters?.reduce((sum, cluster) => sum + cluster.ideas.length, 0) || 0;
});
const entriesCount = computed(() => props.contributions.length);
const analysisLanguage = computed(() => insights.value?.language as Locales | undefined);

const formTitle = computed(() => {
  return getTranslation(props.form.question, 'title', [locale.value as 'en' | 'fr']);
});

const runInsightsGeneration = async (language?: Locales) => {
  emit('analyze', language);
};

const resetAnalysis = async () => {
  $q.dialog({
    title: t('consultation.ai.resetDialog.title'),
    message: t('consultation.ai.resetDialog.message'),
    html: true,
    ok: {
      label: t('consultation.ai.resetDialog.confirm'),
      color: 'negative',
      flat: true,
    },
    cancel: {
      label: t('global.no'),
      color: 'primary',
      unelevated: true,
    },
  }).onOk(() => {
    emit('reset');
  });
};

const confirmReanalyze = async (language?: Locales) => {
  const targetLanguage = language ?? props.selectedAnalysisLanguage ?? analysisLanguage.value;
  const targetLanguageLabel =
    targetLanguage != null ? t(`languages.${targetLanguage}`) : t('consultation.ai.analysisLanguage.unavailable');
  $q.dialog({
    title: t('consultation.ai.reanalyzeDialog.title'),
    message: t('consultation.ai.reanalyzeDialog.messageWithLanguage', { language: targetLanguageLabel }),
    html: true,
    ok: {
      label: t('consultation.ai.reanalyzeDialog.confirm'),
      color: 'primary',
      flat: true,
    },
    cancel: {
      label: t('global.no'),
      color: 'primary',
      unelevated: true,
    },
  }).onOk(() => emit('analyze', targetLanguage ?? undefined));
};

// Theme update handlers from ThemeCard events
function handleUpdateTheme(cluster: Cluster, newValue: string) {
  cluster.theme = newValue;
  save();
}

function handleUpdateIdea(cluster: Cluster, idea: { representative_idea: string }, newValue: string) {
  idea.representative_idea = newValue;
  save();
}

watch(
  () => props.form,
  (form) => {
    if (form && 'ai_insights' in form) {
      insights.value = (form as any).ai_insights as AIInsights | null;
    }
  },
  { immediate: true, deep: false },
);
</script>

<template>
  <!-- Header -->
  <AnalysisHeader
    :title="formTitle"
    :analysis-status="analysisStatus"
    :analysis-language="analysisLanguage"
    :language-enabled="props.languageEnabled"
    :is-analyzing="props.isAnalyzing"
    :is-resetting="props.isResetting"
    @analyze="confirmReanalyze"
    @reset="resetAnalysis"
  />

  <!-- Summary Cards (always shown) -->
  <AnalysisSummaryCards :themes-count="themesCount" :ideas-count="ideasCount" :entries-count="entriesCount" />

  <!-- Empty State -->
  <AnalysisEmptyState
    v-if="analysisStatus === 'not_analyzed' && !isAnalyzing"
    :language-enabled="props.languageEnabled"
    :selected-language="props.selectedAnalysisLanguage"
    @update:selected-language="(language) => emit('update:selectedAnalysisLanguage', language)"
    @analyze="runInsightsGeneration"
  />

  <!-- Analysis Editor -->
  <div v-if="analysisStatus !== 'not_analyzed' || props.isAnalyzing">
    <q-banner v-if="props.error" class="bg-negative text-white q-mb-lg" dense>{{ props.error }}</q-banner>

    <div v-if="analysisStatus === 'analyzed'">
      <!-- Add Theme -->
      <div class="q-mb-md">
        <div v-if="!themeManagement.showNewThemeInput.value" class="row justify-end">
          <q-btn fab-mini icon="add" color="accent" @click="themeManagement.showNewThemeInput.value = true">
            <q-tooltip>{{ t('consultation.ai.themes.add') }}</q-tooltip>
          </q-btn>
        </div>
        <div v-else class="row q-gutter-sm items-center">
          <q-input
            v-model="themeManagement.newThemeName.value"
            :placeholder="t('consultation.ai.themes.new_placeholder')"
            dense
            outlined
            class="col"
            autofocus
            @keyup.enter="themeManagement.createNewTheme()"
            @keyup.esc="themeManagement.cancelNewTheme()"
          />
          <q-btn color="positive" icon="done" @click="themeManagement.createNewTheme()" dense rounded size="sm" />
          <q-btn color="negative" icon="close" @click="themeManagement.cancelNewTheme()" dense rounded size="sm" />
        </div>
      </div>

      <!-- Themes -->
      <div v-if="insights?.clusters && insights.clusters.length > 0">
        <ThemeCard
          v-for="(cluster, idx) in insights.clusters"
          :key="idx"
          :cluster="cluster"
          :drag-over-element="dragDrop.dragOverElement.value"
          :is-drag-disabled="dragDrop.isDragDisabled()"
          @update-theme="handleUpdateTheme"
          @update-idea="handleUpdateIdea"
          @delete-theme="themeManagement.deleteTheme"
          @delete-idea="themeManagement.deleteIdea"
          @dragstart="dragDrop.onDragStart"
          @dragover="dragDrop.onDragOver"
          @dragenter="dragDrop.onDragEnter"
          @dragleave="dragDrop.onDragLeave"
          @drop="dragDrop.onDrop"
          @start-edit="dragDrop.startEdit"
          @finish-edit="dragDrop.finishEdit"
          @move-idea="openMoveIdeaDialog"
          @merge-idea="openMergeIdeaDialog"
          @merge-theme="openMergeThemeDialog"
          @view-contributions="openContributionsDialog"
        />
      </div>
    </div>
  </div>

  <MoveIdeaDialog
    v-model="moveDialog"
    :idea-context="moveIdeaContext"
    :theme-options="moveThemeOptions"
    :selected-theme="moveTargetTheme"
    @update:selected-theme="(val) => (moveTargetTheme = val)"
    @confirm="confirmMoveIdea"
  />

  <MergeThemeDialog
    v-model="mergeThemeDialog"
    :source-theme="mergeSourceTheme"
    :theme-options="mergeThemeOptions"
    :selected-theme="mergeTargetTheme"
    @update:selected-theme="(val) => (mergeTargetTheme = val)"
    @confirm="confirmMergeTheme"
  />

  <MergeIdeaDialog
    v-model="mergeIdeaDialog"
    :idea-context="mergeIdeaContext"
    :options="mergeIdeaOptions"
    :selected-id="mergeIdeaTargetId"
    :search="mergeIdeaSearch"
    @update:selected-id="
      (id) => {
        mergeIdeaTarget = mergeIdeaOptions.find((opt) => opt.id === id)?.value || null;
      }
    "
    @select-option="(val) => (mergeIdeaTarget = val)"
    @update:search="(val) => (mergeIdeaSearch = val)"
    @confirm="confirmMergeIdea"
  />

  <ContributionsDialog
    v-model="contributionsDialog"
    :idea-context="contributionsContext"
    :contributions="contributionsList"
    :expanded="expandedContributions"
    @toggle="toggleContribution"
  />
</template>
